import React, { Suspense, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import DrawerV6 from '../../Common/Drawer/DrawerV6/DrawerV6';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Modal61 from '../../Common/Modal/Modal61';
import InputText from '../../Common/InputText';
import ColorChooser from '../../Common/ColorChooser';
import Tag from '../../Common/Tag/Tag';
import MultiSelectBoxV6 from '@Components/Common/MultiSelectBoxV6/MultiSelectBoxV6';
import ToggleV6 from '@Components/Common/ToggleV6/ToggleV6';
import { useLoadBulkUpdateAttributeFilters, useBulkUpdateAttributes } from '../../../apis/bulkUpdateAttributes';
import { TaskTagOption } from '../../../apis/bulkUpdateAttributes/bulkUpdateAttributes.types';
import { useSaveTaskTag } from '../../../apis/taskTags';
import './BulkUpdateAttributesDrawer.scss';

const ADD_NEW_TAG_OPTION_ID = '-9999';

const ADD_NEW_TAG_OPTION: TaskTagOption = {
	id: -9999,
	name: '+ Add New Task Tag',
	styles: {
		backgroundColor: '#F7F5FF',
		color: '#46389E',
	},
};

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary,
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong loading bulk update attributes:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Loading Component for Suspense
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading...</div>
	</LoadingIndicator>
);

interface CheckedTask {
	id: number;
}

interface ComponentProps {
	baselineId: string;
	onComplete: () => void;
	onCancel: () => void;
	checkedTasks: CheckedTask[];
}

interface NewTaskTagForm {
	name: string;
	bgColor: string;
	fontColor: string;
}

interface Modal61Handle {
	show: () => void;
	hide: () => void;
}

const BulkUpdateAttributesDrawerContent: React.FC<ComponentProps> = ({ baselineId, onComplete, onCancel, checkedTasks }) => {
	const [isTrickleDown, setIsTrickleDown] = useState<boolean>(true);
	const [departmentId, setDepartmentId] = useState<string>('');
	const [jobTitleId, setJobTitleId] = useState<string>('');
	const [communityId, setCommunityId] = useState<string>('');
	const [phaseId, setPhaseId] = useState<string>('');
	const [taskTagIds, setTaskTagIds] = useState<string>('');
	const [isMilestone, setIsMilestone] = useState<string>('');
	const [newTaskTag, setNewTaskTag] = useState<NewTaskTagForm>({ name: '', bgColor: '#5F0DF0', fontColor: '#ffffff' });
	const [isNewTagFormValid, setIsNewTagFormValid] = useState<boolean>(true);
	const tagModalRef = useRef<Modal61Handle>(null);

	useEffect(() => {
		if (checkedTasks.length < 1) {
			Notification('error', 'Error', 'At least one location must be selected');
			onCancel();
		}
		// only check on mount, matching the previous componentDidMount behavior
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { data: filtersData, isFetching: isLoadingFilters, error: filtersError } = useLoadBulkUpdateAttributeFilters(baselineId, { enabled: checkedTasks.length > 0 });

	useEffect(() => {
		if (!filtersError && !filtersData?.isError) return;
		// Without this the attribute dropdowns just render empty, which is
		// indistinguishable from "this baseline has none".
		Notification(
			'error',
			'An Error Occurred',
			filtersData?.errors?.[0]?.message ?? filtersError?.message ?? 'Failed to load attribute filters'
		);
	}, [filtersError, filtersData]);

	const tags = useMemo(() => [...(filtersData?.tags ?? []), ADD_NEW_TAG_OPTION], [filtersData?.tags]);

	const bulkUpdateMutation = useBulkUpdateAttributes((result) => {
		if (result.isError) {
			Notification('error', 'Error', result.errors?.[0]?.message ?? 'Failed to update attributes');
			return;
		}
		Notification('success', 'Success', 'Attributes updated successfully');
		onComplete();
	});

	// parseMutationResponse throws when the server reports isError, so failures arrive
	// through the mutate() onError callback below, never as a successful result.
	const saveTaskTagMutation = useSaveTaskTag(() => {
		Notification('success', 'Save Successful', 'Task Tag created');
		tagModalRef.current?.hide();
	});

	const onSave = useCallback(() => {
		if (!departmentId && !communityId && !jobTitleId && !phaseId && !taskTagIds && !isMilestone) {
			Notification('error', 'Error', 'You must change at least one attribute');
			return;
		}

		bulkUpdateMutation.mutate(
			{
				baselineTaskIds: checkedTasks.map((t) => t.id),
				trickleDown: isTrickleDown,
				// 0, not null, for "not being changed" - the wire format the deleted class component
				// sent. BulkUpdateAttributes.UpdateTaskAttributes skips both null and 0, so this is
				// format parity with the old client rather than a behavioural difference.
				departmentId: departmentId ? Number(departmentId) : 0,
				communityId: communityId ? Number(communityId) : 0,
				jobTitleId: jobTitleId ? Number(jobTitleId) : 0,
				phaseId: phaseId ? Number(phaseId) : 0,
				taskTagIds: taskTagIds ? taskTagIds.split(',') : undefined,
				isMilestone: isMilestone === 'true' || isMilestone === 'false' ? isMilestone : null,
			},
			{
				onError: (error) => {
					Notification('error', 'An Error Occurred', error.message);
				},
			}
		);
	}, [departmentId, communityId, jobTitleId, phaseId, taskTagIds, isTrickleDown, isMilestone, checkedTasks, bulkUpdateMutation]);

	const onMultiSelectChange = useCallback((value: string, name: string) => {
		if (name === 'taskTagIds' && value.includes(ADD_NEW_TAG_OPTION_ID)) {
			tagModalRef.current?.show();
			return;
		}

		switch (name) {
			case 'departmentId': setDepartmentId(value); break;
			case 'jobTitleId': setJobTitleId(value); break;
			case 'communityId': setCommunityId(value); break;
			case 'phaseId': setPhaseId(value); break;
			case 'taskTagIds': setTaskTagIds(value); break;
			case 'isMilestone': setIsMilestone(value); break;
		}
	}, []);

	const onToggleChange = useCallback((_name: string, isChecked: boolean) => {
		setIsTrickleDown(isChecked);
	}, []);

	const onCreateNewTaskTag = useCallback(() => {
		if (newTaskTag.name.trim().length < 1) {
			setIsNewTagFormValid(false);
			return;
		}

		saveTaskTagMutation.mutate(
			{
				id: 0,
				name: newTaskTag.name,
				fontColor: newTaskTag.fontColor,
				backgroundColor: newTaskTag.bgColor,
				isActive: true,
			},
			{
				onError: (error) => {
					Notification('error', 'An Error Occurred', error.message);
				},
			}
		);
	}, [newTaskTag, saveTaskTagMutation]);

	const onNewTagFormChange = useCallback((name: string, value: string) => {
		setNewTaskTag((prev) => ({ ...prev, [name]: value }));
	}, []);

	const closeTagModal = useCallback(() => {
		tagModalRef.current?.hide();
	}, []);

	const isLoading = isLoadingFilters || bulkUpdateMutation.isPending;

	return (
		<DrawerV6 isWide={false} title="Update Attributes" onSaveClick={onSave} saveText="Update" onCancelClick={onCancel}>
			<LoadingIndicator isLoading={isLoading}>
				<form className="update-attr-drawer" noValidate>
					<div className="row">
						<div className="col">
							<MultiSelectBoxV6
								name="departmentId"
								label="Departments"
								idField="id"
								valueField="name"
								options={filtersData?.departments ?? []}
								selectedValues={departmentId}
								onChange={onMultiSelectChange}
								isRequired={false}
								isMulti={false}
							/>
							<MultiSelectBoxV6
								name="jobTitleId"
								label="Job Titles"
								idField="id"
								valueField="name"
								options={filtersData?.baselineJobTitles ?? []}
								selectedValues={jobTitleId}
								onChange={onMultiSelectChange}
								isRequired={false}
								isMulti={false}
							/>
							<MultiSelectBoxV6
								name="communityId"
								label="Communities"
								idField="id"
								valueField="name"
								options={filtersData?.communities ?? []}
								selectedValues={communityId}
								onChange={onMultiSelectChange}
								isRequired={false}
								isMulti={false}
							/>
							<MultiSelectBoxV6
								name="phaseId"
								label="Phases"
								idField="id"
								valueField="name"
								options={filtersData?.phases ?? []}
								selectedValues={phaseId}
								onChange={onMultiSelectChange}
								isRequired={false}
								isMulti={false}
							/>
							<MultiSelectBoxV6
								name="taskTagIds"
								options={tags}
								idField="id"
								valueField="name"
								selectedValues={taskTagIds}
								onChange={onMultiSelectChange}
								isMulti={true}
								label="Additional Task Tags"
							/>
							<MultiSelectBoxV6
								name="isMilestone"
								label="Milestone"
								options={[
									{ id: 'true', name: 'Update selected to be a Milestone' },
									{ id: 'false', name: 'Update selected to Not be a Milestone' },
								]}
								idField="id"
								valueField="name"
								selectedValues={isMilestone}
								onChange={onMultiSelectChange}
								isRequired={false}
								isMulti={false}
							/>
							<div className="row">
								<ToggleV6
									name="isTrickleDown"
									text="Update location tasks with new selection"
									checked={isTrickleDown}
									onChange={onToggleChange}
								/>
							</div>
						</div>
					</div>
					<Modal61
						title="Create New Task Tag"
						ref={tagModalRef}
						body={
							<form name="newProjectManagerForm" noValidate className={isNewTagFormValid ? '' : 'was-validated'}>
								<div className="form-group">
									<label htmlFor="project-tag-name" className="title">
										Task Tag Name
									</label>
									<InputText
										name="project-tag-name"
										type="text"
										value={newTaskTag.name}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNewTagFormChange('name', e.target.value)}
										isRequired={true}
										errorMessage="Task Tag Name is required"
										maxLength={50}
									/>
									<small id="project-tag-name-help" className="form-text text-muted sub-title">
										Character Limit 50
									</small>
								</div>
								<ColorChooser
									name="backgroundColor"
									title="Background Color"
									selectedColor={newTaskTag.bgColor}
									onChange={(color: string) => onNewTagFormChange('bgColor', color)}
								/>
								<ColorChooser
									name="fontColor"
									title="Font Color"
									selectedColor={newTaskTag.fontColor}
									onChange={(color: string) => onNewTagFormChange('fontColor', color)}
								/>
								<div className="mt-5">
									<span className="me-2">Preview:</span>
									<Tag title={newTaskTag.name} backgroundColor={newTaskTag.bgColor} color={newTaskTag.fontColor} />
								</div>
							</form>
						}
						primaryButtonText="Save"
						onPrimaryClick={onCreateNewTaskTag}
						showSecondaryButton={true}
						onSecondaryClick={closeTagModal}
						onCloseClick={closeTagModal}
						isLoading={saveTaskTagMutation.isPending}
					/>
				</form>
			</LoadingIndicator>
		</DrawerV6>
	);
};

const BulkUpdateAttributesDrawer: React.FC<ComponentProps> = (props) => {
	return (
		<ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.reload()}>
			<Suspense fallback={<LoadingComponent />}>
				<BulkUpdateAttributesDrawerContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
};

export default BulkUpdateAttributesDrawer;
